import { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
    FormHelperText, Table, TableHead, TableRow, TableCell, TableBody,
    TableContainer, Paper, Pagination,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { getAll as getAllDrives } from '../../../../services/driveService';
import { getAll as getAllCourses } from '../../../../services/courseService';
import { getAll as getAllBatches } from '../../../../services/batchService';
import { getAll } from '../../../../services/masterService';
import { getById as getConfigById } from '../../../../services/configurationService';
import { getAssignedSections, createEvaluatorSection, updateEvaluatorSection, allocateCandidate } from '../../../../services/evaluatorService';
import { useToaster } from '../../../../providers/ToasterProvider';

// ─── Constants ────────────────────────────────────────────────────────────────

const SEC_LIST_PAGE_SIZE = 5;

// ─── Component ────────────────────────────────────────────────────────────────

export const EvaluatorSectionsPage = ({ userDetails }) => {
    const { addNotification } = useToaster();
    const [isLoading, setIsLoading] = useState(false);
    const [isAllocateLoading, setIsAllocateLoading] = useState(false);

    // ── Lookup data ───────────────────────────────────────────────────────
    const [drives, setDrives] = useState([]);
    const [allCourses, setAllCourses] = useState([]);
    const [allBatches, setAllBatches] = useState([]);
    const [allProjectMasters, setAllProjectMasters] = useState([]);

    // ── Form state ────────────────────────────────────────────────────────
    const [driveId, setDriveId] = useState(0);
    const [courseId, setCourseId] = useState(0);
    const [batchId, setBatchId] = useState(0);
    const [availableSections, setAvailableSections] = useState([]);
    const [selectedSection, setSelectedSection] = useState('');
    const [sectionsError, setSectionsError] = useState(false);
    const [candidateLimit, setCandidateLimit] = useState('');
    const [candidateLimitError, setCandidateLimitError] = useState('');

    // ── Assigned sections list ────────────────────────────────────────────
    const [assignedData, setAssignedData] = useState([]);
    const [secListPage, setSecListPage] = useState(1);

    const handlePageChange = (event, value) => {
        setSecListPage(value);
    };

    // ─── Derived dropdown options (no cascade useEffect needed) ──────────

    // Drive → filtered courses
    const courses = useMemo(() => {
        if (!driveId) return [];
        const result = [];
        for (const pm of allProjectMasters) {
            if (pm.driveId === driveId) {
                const c = allCourses.find((c) => c.id === pm.courseId);
                if (c && !result.some((x) => x.id === c.id)) result.push(c);
            }
        }
        return result;
    }, [driveId, allProjectMasters, allCourses]);

    // Drive + Course → filtered batches
    const batches = useMemo(() => {
        if (!driveId || !courseId) return [];
        const result = [];
        for (const pm of allProjectMasters) {
            if (pm.driveId === driveId && pm.courseId === courseId) {
                const b = allBatches.find((b) => b.id === pm.batchId);
                if (b && !result.some((x) => x.id === b.id)) result.push(b);
            }
        }
        return result;
    }, [driveId, courseId, allProjectMasters, allBatches]);

    // ─── Load everything when the Sections page first mounts ─────────────

    useEffect(() => {
        if (!userDetails?.id) return;

        const load = async () => {
            try {
                const [driveRes, courseRes, batchRes, pmRes, assignedRes] = await Promise.all([
                    getAllDrives(), getAllCourses(), getAllBatches(), getAll(),
                    getAssignedSections(userDetails.id),
                ]);

                if (driveRes.code === 'OK') setDrives(driveRes.data);
                if (courseRes.code === 'OK') setAllCourses(courseRes.data);
                if (batchRes.code === 'OK') setAllBatches(batchRes.data);
                if (pmRes.code === 'OK') setAllProjectMasters(pmRes.data.list);

                if (assignedRes.code === 'OK' && assignedRes.data.sections) {
                    const rows = Array.isArray(assignedRes.data.sections)
                        ? assignedRes.data.sections
                        : [assignedRes.data.sections];
                    setAssignedData(rows);

                    // Pre-fill form from the first assigned section.
                    // All IDs are set in one batch — no setTimeout needed because
                    // courses/batches are now useMemo (reactive), not useEffect state.
                    const first = rows[0];
                    if (first) {
                        setDriveId(first.driveId ?? 0);
                        setCourseId(first.courseId ?? 0);
                        setBatchId(first.batchId ?? 0);
                        setSelectedSection(first.section ?? first.sectionName ?? '');
                        if (first.candidateLimit != null) setCandidateLimit(String(first.candidateLimit));
                    }
                }
            } catch (err) {
                console.log('Error loading sections data', err);
            }
        };

        load();
    }, [userDetails?.id]);

    // ─── Fetch available sections whenever drive/course/batch change ──────

    useEffect(() => {
        if (!driveId || !courseId || !batchId) {
            setAvailableSections([]);
            return;
        }
        console.log('Finding config for Drive ID', driveId, 'Course ID', courseId, 'Batch ID', batchId);
        const matchedPm = allProjectMasters.find((pm) =>
            pm.driveId === driveId && pm.courseId === courseId && pm.batchId === batchId
        );
        console.log('Matched PM for section options:', matchedPm);
        if (!matchedPm?.configurationId) { setAvailableSections([]); return; }
        getConfigById(matchedPm.configurationId)
            .then((res) => {
                if (res.code === 'OK') {
                    const secs = (res.data?.sections ?? []).map((s) => s.sectionTitle).filter(Boolean);
                    setAvailableSections(secs);
                }
            })
            .catch(() => setAvailableSections([]));
    }, [driveId, courseId, batchId, allProjectMasters]);

    const sectionOptions = availableSections.length > 0
        ? ['all', ...availableSections]
        : (batchId ? ['default'] : []);

    // ─── Assigned list (client-side pagination) ───────────────────────────

    const secListMaxPages = Math.max(1, Math.ceil(assignedData.length / SEC_LIST_PAGE_SIZE));
    const secListRows = assignedData.slice(
        (secListPage - 1) * SEC_LIST_PAGE_SIZE,
        secListPage * SEC_LIST_PAGE_SIZE
    );

    const refreshAssigned = async () => {
        try {
            const res = await getAssignedSections(userDetails.id);
            if (res.code === 'OK' && res.data.sections) {
                const rows = Array.isArray(res.data.sections)
                    ? res.data.sections
                    : [res.data.sections];
                setAssignedData(rows);
                setSecListPage(1);
            }
        } catch (err) {
            console.log('Error refreshing assigned sections', err);
        }
    };

    // ─── Submit ───────────────────────────────────────────────────────────

    const onSubmit = async () => {
        let hasError = false;
        const limitErr = !candidateLimit ? 'Candidate limit is required.'
            : (isNaN(candidateLimit) || Number(candidateLimit) <= 0 || !Number.isInteger(Number(candidateLimit)))
                ? 'Please enter a valid positive number.' : '';
        if (limitErr) { setCandidateLimitError(limitErr); hasError = true; }
        if (!selectedSection) { setSectionsError(true); hasError = true; }
        if (!driveId || !courseId || !batchId) {
            addNotification('Please fill all required fields.', 'warning');
            hasError = true;
        }
        if (hasError) return;

        // POST for new assignment; PUT (limit only) if already assigned
        const existingSection = assignedData.find((entry) =>
            entry.driveId === driveId &&
            entry.courseId === courseId &&
            entry.batchId === batchId &&
            (entry.section === selectedSection || entry.sectionName === selectedSection)
        );

        setIsLoading(true);
        try {
            let res;
            if (existingSection) {
                res = await updateEvaluatorSection(existingSection.id, {
                    candidateLimit: Number(candidateLimit),
                });
            } else {
                res = await createEvaluatorSection({
                    userId: userDetails.id,
                    driveId,
                    courseId,
                    batchId,
                    sectionName: selectedSection,
                    candidateLimit: Number(candidateLimit),
                });
            }

            if (res.code === 'OK' || res.code === 'CREATED') {
                addNotification(
                    existingSection ? 'Candidate limit updated successfully' : 'Section assigned successfully',
                    'success'
                );
                await refreshAssigned();
            }
        } catch (err) {
            const apiMsg = err?.response?.data?.message;
            if (err?.response?.status === 400) {
                addNotification(
                    apiMsg || `Section "${selectedSection}" is already assigned to this evaluator for the selected exam.`,
                    'danger'
                );
            } else {
                console.log('Error updating sections', err);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Allocate Candidate ───────────────────────────────────────────────

    const onAllocateCandidate = async () => {
        if (!userDetails?.id) return;
        setIsAllocateLoading(true);
        try {
            const res = await allocateCandidate({ evaluatorId: userDetails.id });
            if (res.code === 'OK') {
                if (res.data?.totalAssigned === 0) {
                    addNotification('No Candidates found for allocation.', 'warning');
                } else {
                    addNotification('Candidates allocated successfully.', 'success');
                    await refreshAssigned();
                }
            }
        } catch (err) {
            const apiMsg = err?.response?.data?.message;
            addNotification(apiMsg || 'Failed to allocate candidates.', 'danger');
            console.log('Error allocating candidates', err);
        } finally {
            setIsAllocateLoading(false);
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────

    return (
        <Box sx={{ p: 4 }}>

            {/* ── Assignment form ── */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 3 }}>

                <FormControl sx={{ minWidth: 160, flex: '1 1 140px' }}>
                    <InputLabel id='secDriveLabel'>Drive<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></InputLabel>
                    <Select labelId='secDriveLabel' label='Drive*' value={driveId}
                        onChange={(e, ev) => {
                            setDriveId(ev.props.value);
                            setCourseId(0);
                            setBatchId(0);
                            setAvailableSections([]);
                            setSelectedSection('');
                            setCandidateLimit('');
                            setCandidateLimitError('');
                        }}>
                        <MenuItem value={0}>None</MenuItem>
                        {drives.map((d) => <MenuItem key={d.id} value={d.id}>{d.driveName}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 160, flex: '1 1 140px' }}>
                    <InputLabel id='secCourseLabel'>Course<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></InputLabel>
                    <Select labelId='secCourseLabel' label='Course*' value={courseId}
                        onChange={(e, ev) => {
                            setCourseId(ev.props.value);
                            setBatchId(0);
                            setAvailableSections([]);
                            setSelectedSection('');
                            setCandidateLimit('');
                            setCandidateLimitError('');
                        }}
                        disabled={!driveId}>
                        <MenuItem value={0}>None</MenuItem>
                        {courses.map((c) => <MenuItem key={c.id} value={c.id}>{c.courseName}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 160, flex: '1 1 140px' }}>
                    <InputLabel id='secBatchLabel'>Batch<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></InputLabel>
                    <Select labelId='secBatchLabel' label='Batch*' value={batchId}
                        onChange={(e, ev) => {
                            setBatchId(ev.props.value);
                            setSelectedSection('');
                            setCandidateLimit('');
                            setCandidateLimitError('');
                        }}
                        disabled={!courseId}>
                        <MenuItem value={0}>None</MenuItem>
                        {batches.map((b) => <MenuItem key={b.id} value={b.id}>{b.batchName}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 200, flex: '2 1 180px' }} error={sectionsError}>
                    <InputLabel id='secSectionLabel'>
                        Section<Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                    </InputLabel>
                    <Select labelId='secSectionLabel' label='Section*' value={selectedSection}
                        onChange={(e) => {
                            setSelectedSection(e.target.value);
                            if (sectionsError) setSectionsError(false);
                            setCandidateLimit('');
                            setCandidateLimitError('');
                        }}
                        disabled={!batchId}>
                        <MenuItem value=''>None</MenuItem>
                        {sectionOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                    {sectionsError && <FormHelperText>Please select a section</FormHelperText>}
                </FormControl>

                <TextField sx={{ minWidth: 140, flex: '1 1 120px' }}
                    label={<><Box component='span'>Limit</Box><Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box></>}
                    variant='outlined' type='number' inputProps={{ min: 1 }}
                    value={candidateLimit}
                    onChange={(e) => { setCandidateLimit(e.target.value); if (candidateLimitError) setCandidateLimitError(''); }}
                    error={Boolean(candidateLimitError)} helperText={candidateLimitError} />

            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 4 }}>
                <LoadingButton sx={{ textTransform: 'uppercase', borderRadius: 40 }}
                    color='primary' variant='contained' size='large'
                    loading={isLoading} onClick={onSubmit}>
                    Submit
                </LoadingButton>
            </Box>

            {/* ── Assigned Sections List ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                    Assigned Sections
                </Typography>
                <LoadingButton sx={{ textTransform: 'uppercase', borderRadius: 40 }}
                    color='primary' variant='contained' size='small'
                    loading={isAllocateLoading} onClick={onAllocateCandidate}>
                    Allocate Candidate
                </LoadingButton>
            </Box>

            <Paper sx={{
                width: '100%',
                overflow: 'hidden',
                bgcolor: 'background.base',
                maxHeight: 'calc(100vh - 305px)',
                maxWidth: 'calc(100vw - 140px)',
                borderRadius: 1,
                mb: 4
            }}>
                <TableContainer sx={{
                    px: 1,
                    pb: 1,
                    maxHeight: 'calc(100vh - 365px)',
                    maxWidth: 'inherit',
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1
                }}>
                    <Table stickyHeader aria-label='assigned sections table'>
                        <TableHead>
                            <TableRow>
                                {['Drive', 'Course', 'Batch', 'Section', 'Candidate Limit','Assign Candidate','In Progress','Pending','Completed'].map((h) => (
                                    <TableCell key={h} align='center' sx={{ color: 'primary.main', px: 1.5 }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!secListRows.length ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ border: 'none', textAlign: 'center', color: 'grey.500' }}>
                                        No sections assigned
                                    </TableCell>
                                </TableRow>
                            ) : secListRows.map((row, idx) => {
                                const driveName = drives.find((d) => d.id === row.driveId)?.driveName ?? row['drive.driveName'] ?? row.driveId;
                                const courseName = allCourses.find((c) => c.id === row.courseId)?.courseName ?? row['course.courseName'] ?? row.courseId;
                                const batchName = allBatches.find((b) => b.id === row.batchId)?.batchName ?? row['batch.batchName'] ?? row.batchId;
                                return (
                                    <TableRow key={idx} hover={false}>
                                        <TableCell align='center' sx={{ px: 1.5, py: 0.75 }}>{driveName}</TableCell>
                                        <TableCell align='center' sx={{ px: 1.5, py: 0.75 }}>{courseName}</TableCell>
                                        <TableCell align='center' sx={{ px: 1.5, py: 0.75 }}>{batchName}</TableCell>
                                        <TableCell align='center' sx={{ px: 1.5, py: 0.75 }}>{row.section ?? row.sectionName}</TableCell>
                                        <TableCell align='center' sx={{ px: 1.5, py: 0.75 }}>{row.candidateLimit}</TableCell>
                                        <TableCell align='center' sx={{ px: 1.5, py: 0.75 }}>{row.assignedCandidateCount}</TableCell>
                                        <TableCell align='center' sx={{ px: 1.5, py: 0.75 }}>{row.inProgressCandidateCount}</TableCell>
                                        <TableCell align='center' sx={{ px: 1.5, py: 0.75 }}>{row.pendingCandidateCount}</TableCell>
                                        <TableCell align='center' sx={{ px: 1.5, py: 0.75 }}>{row.completedCandiateCount}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2.5, mt: 'auto' }}>
                <Pagination
                    sx={{ ml: 'auto' }}
                    count={secListMaxPages}
                    page={secListPage}
                    onChange={handlePageChange}
                    shape='rounded'
                />
            </Box>

        </Box>
    );
};
